import { useState, useRef, useEffect } from "react";
import { supabase } from "./supabase";
import Auth from "./Auth";
import { exportToPDF } from "./ExportPDF";

const API_URL = "https://ohada-backend.onrender.com";

const SUGGESTED = [
  { fr: "Comment créer une SARL dans l'espace OHADA ?", en: "How to create an LLC under OHADA law?" },
  { fr: "Quelles sûretés sont reconnues par l'Acte Uniforme sur les Sûretés ?", en: "What securities are recognized under the AUS?" },
  { fr: "Comment fonctionne l'arbitrage devant la CCJA ?", en: "How does CCJA arbitration work?" },
  { fr: "Quelles sont les conditions d'une procédure collective ?", en: "What triggers OHADA insolvency proceedings?" },
];

const FLAGS = ["🇧🇯","🇧🇫","🇨🇲","🇨🇫","🇹🇩","🇰🇲","🇨🇬","🇨🇮","🇨🇩","🇬🇶","🇬🇦","🇬🇳","🇬🇼","🇲🇱","🇳🇪","🇸🇳","🇹🇬"];

function MD({ text }) {
  const html = text
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/^## (.*)/gm, '<p class="h2">$1</p>')
    .replace(/^# (.*)/gm, '<p class="h1">$1</p>')
    .replace(/^---$/gm, '<hr class="mhr"/>')
    .replace(/^- (.*)/gm, '<p class="li">▸ $1</p>')
    .replace(/`([^`]+)`/g, '<code class="mc">$1</code>')
    .replace(/(DISCLAIMER|Disclaimer)(.*)/g, '<p class="disc">⚠️ <em>$1$2</em></p>')
    .replace(/\n\n/g, '<p class="gap"></p>')
    .replace(/\n/g, "<br/>");
  return <div className="mdbody" dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function App() {
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [lang, setLang] = useState("fr");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [tab, setTab] = useState("chat");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user || null);
      setCheckingAuth(false);
    });
    supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });
  }, []);

  useEffect(() => {
    if (user) loadHistory();
  }, [user]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const loadHistory = async () => {
    const { data } = await supabase
      .from("conversations")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(20);
    setHistory(data || []);
  };

  const saveConversation = async (question, answer, sources, decisions_cited) => {
    if (!user) return;
    await supabase.from("conversations").insert({
      user_id: user.id,
      question,
      answer,
      sources,
      decisions_cited,
      lang,
    });
    loadHistory();
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setMessages([]);
    setHistory([]);
  };

  const sendMessage = async (text) => {
    const userText = (text || input).trim();
    if (!userText && !file) return;
    if (loading) return;
    const displayText = userText || (lang === "fr" ? "Analyser ce document selon le droit OHADA." : "Analyze this document under OHADA law.");
    const newMsg = { role: "user", content: displayText, fileLabel: file?.name };
    const history2 = [...messages, newMsg];
    setMessages(history2);
    setInput("");
    setLoading(true);
    const savedFile = file;
    setFile(null);
    try {
      if (savedFile) {
        const text2 = await savedFile.text();
        const res = await fetch(`${API_URL}/audit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ document_text: text2, document_name: savedFile.name, lang }),
        });
        const data = await res.json();
        setMessages(prev => [...prev, { role: "assistant", content: data.audit, sources: [] }]);
        saveConversation(displayText, data.audit, [], 0);
      } else {
        const res = await fetch(`${API_URL}/ask`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ question: userText, lang }),
        });
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let answer = "";
        let sources = [];
        let decisions_cited = 0;
        setMessages(prev => [...prev, { role: "assistant", content: "", sources: [], decisions_cited: 0 }]);
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value);
          const lines = chunk.split("\n");
          for (const line of lines) {
            if (line.startsWith("data: ") && line !== "data: [DONE]") {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === "sources") {
                  sources = data.sources;
                  decisions_cited = data.decisions_cited;
                } else if (data.type === "text") {
                  answer += data.text;
                  setMessages(prev => {
                    const updated = [...prev];
                    updated[updated.length - 1] = { role: "assistant", content: answer, sources, decisions_cited };
                    return updated;
                  });
                }
              } catch {}
            }
          }
        }
        saveConversation(userText, answer, sources, decisions_cited);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: lang === "fr" ? "Erreur réseau. Veuillez réessayer." : "Network error. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  if (checkingAuth) return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:"#080d1a",color:"#c9a84c",fontSize:"2em"}}>⚖</div>;
  if (!user) return <Auth onLogin={setUser} />;

  return (
    <div style={styles.root}>
      <style>{CSS}</style>
      <header style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.brand}>
            <span style={styles.brandIcon}>⚖</span>
            <div>
              <div style={styles.brandName}>OHADA Expert</div>
              <div style={styles.brandSub}>3 833 décisions CCJA · Intelligence Juridique IA</div>
            </div>
          </div>
          <div style={styles.headerRight}>
            <div style={styles.langSw}>
              {["fr","en"].map(l => (
                <button key={l} style={{...styles.lbtn, ...(lang===l?styles.lbtnOn:{})}} onClick={() => setLang(l)}>{l.toUpperCase()}</button>
              ))}
            </div>
            <button style={styles.histBtn} onClick={() => setShowHistory(!showHistory)}>📋</button>
            <button style={styles.logoutBtn} onClick={handleLogout}>↩</button>
          </div>
        </div>
        <div style={styles.flagsRow}>
          {FLAGS.map((f,i) => <span key={i} style={styles.flag}>{f}</span>)}
          <span style={styles.flagLbl}>17 États membres</span>
        </div>
        <nav style={styles.tabs}>
          {[
            {id:"chat", icon:"💬", fr:"Assistant", en:"Assistant"},
            {id:"audit", icon:"📄", fr:"Audit Document", en:"Document Audit"},
          ].map(t => (
            <button key={t.id} style={{...styles.tab, ...(tab===t.id?styles.tabOn:{})}} onClick={() => { setTab(t.id); setShowHistory(false); }}>
              {t.icon} {t[lang]}
            </button>
          ))}
        </nav>
      </header>

      {showHistory && (
        <div style={styles.historyPanel}>
          <div style={styles.historyTitle}>📋 {lang==="fr" ? "Historique des consultations" : "Consultation History"}</div>
          {history.length === 0 ? (
            <div style={styles.historyEmpty}>{lang==="fr" ? "Aucune consultation encore." : "No consultations yet."}</div>
          ) : (
            history.map((h,i) => (
              <div key={i} style={styles.historyItem} onClick={() => {
                setMessages([
                  { role: "user", content: h.question },
                  { role: "assistant", content: h.answer, sources: h.sources || [], decisions_cited: h.decisions_cited }
                ]);
                setShowHistory(false);
                setTab("chat");
              }}>
                <div style={styles.historyQ}>{h.question?.slice(0,80)}...</div>
                <div style={styles.historyDate}>{new Date(h.created_at).toLocaleDateString()}</div>
              </div>
            ))
          )}
        </div>
      )}

      <main style={styles.main}>
        {messages.length === 0 && tab === "chat" && !showHistory && (
          <div style={styles.welcome}>
            <div style={styles.wicon}>⚖</div>
            <h1 style={styles.wtitle}>OHADA Expert</h1>
            <p style={styles.wdesc}>
              {lang==="fr"
                ? `Bienvenue ${user.email}! Votre assistant juridique IA alimenté par 3 833 décisions réelles de la CCJA.`
                : `Welcome ${user.email}! Your AI legal assistant powered by 3,833 real CCJA decisions.`}
            </p>
            <div style={styles.sgrid}>
              {SUGGESTED.map((q,i) => (
                <button key={i} style={styles.sbtn} onClick={() => sendMessage(q[lang])}>{q[lang]}</button>
              ))}
            </div>
          </div>
        )}
        {messages.length === 0 && tab === "audit" && (
          <div style={styles.welcome}>
            <div style={styles.wicon}>📄</div>
            <h1 style={styles.wtitle}>{lang==="fr" ? "Audit Juridique OHADA" : "OHADA Legal Audit"}</h1>
            <p style={styles.wdesc}>
              {lang==="fr"
                ? "Cliquez sur 📎 pour déposer un contrat ou document juridique."
                : "Click 📎 to upload a contract or legal document."}
            </p>
          </div>
        )}
        <div style={styles.msgs}>
          {messages.map((m,i) => (
            <div key={i} style={{...styles.mrow, ...(m.role==="user"?styles.mrowUser:{})}}>
              {m.role==="assistant" && <div style={styles.avAi}>⚖</div>}
              <div style={{...styles.bubble, ...(m.role==="user"?styles.bubbleUser:styles.bubbleAi)}}>
                {m.fileLabel && <div style={styles.dbadge}>📄 {m.fileLabel}</div>}
                {m.role==="assistant" ? <MD text={m.content}/> : m.content}
                {m.sources && m.sources.length > 0 && (
                  <div style={styles.sources}>
                    <div style={styles.sourcesLabel}>📚 {m.decisions_cited} {lang==="fr"?"décisions CCJA citées":"CCJA decisions cited"}</div>
                    {m.sources.map((s,j) => s.url && (
                      <a key={j} href={s.url} target="_blank" rel="noreferrer" style={styles.sourceLink}>
                        {s.arret || `Décision ${j+1}`} →
                      </a>
                    ))}
                  </div>
                )}
                {m.role === "assistant" && m.content && (
                  <button
                    style={styles.exportBtn}
                    onClick={() => exportToPDF({
                      question: messages[i-1]?.content || "",
                      answer: m.content,
                      sources: m.sources || [],
                      decisions_cited: m.decisions_cited || 0,
                      userEmail: user?.email,
                      lang
                    })}
                  >
                    📄 {lang === "fr" ? "Exporter en PDF" : "Export to PDF"}
                  </button>
                )}
              </div>
              {m.role==="user" && <div style={styles.avUser}>👤</div>}
            </div>
          ))}
          {loading && (
            <div style={styles.mrow}>
              <div style={styles.avAi}>⚖</div>
              <div style={{...styles.bubble, ...styles.bubbleAi, display:"flex", alignItems:"center", gap:6}}>
                <span className="d1">●</span><span className="d2">●</span><span className="d3">●</span>
                <span style={styles.loadingTxt}>{lang==="fr"?"Analyse en cours…":"Analyzing…"}</span>
              </div>
            </div>
          )}
          <div ref={bottomRef}/>
        </div>
      </main>

      <footer style={styles.footer}>
        {file && (
          <div style={styles.filePrev}>
            <span>📄 {file.name}</span>
            <button style={styles.fileRm} onClick={() => setFile(null)}>✕</button>
          </div>
        )}
        <div style={styles.inputRow}>
          <button style={styles.attachBtn} onClick={() => fileRef.current?.click()}>📎</button>
          <input ref={fileRef} type="file" style={{display:"none"}} accept=".txt,.pdf,.doc,.docx"
            onChange={e => { setFile(e.target.files[0]); setTab("audit"); e.target.value=""; }}/>
          <textarea
            style={styles.txa}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder={lang==="fr" ? "Posez votre question OHADA…" : "Ask your OHADA question…"}
            rows={1}
          />
          <button
            style={{...styles.sendBtn, ...((!input.trim()&&!file)||loading?styles.sendOff:{})}}
            onClick={() => sendMessage()}
            disabled={(!input.trim()&&!file)||loading}
          >➤</button>
        </div>
        <div style={styles.fnote}>
          {lang==="fr" ? "Outil informatif · Consultez un avocat pour tout conseil officiel" : "Informational tool · Consult a lawyer for official advice"}
        </div>
      </footer>
    </div>
  );
}

const styles = {
  root:{display:"flex",flexDirection:"column",height:"100vh",background:"#080d1a",color:"#ddd5c0",fontFamily:"Georgia, serif",overflow:"hidden"},
  header:{background:"rgba(5,9,20,0.97)",borderBottom:"1px solid rgba(201,168,76,0.22)",padding:"12px 18px 0",flexShrink:0},
  headerTop:{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8},
  brand:{display:"flex",alignItems:"center",gap:12},
  brandIcon:{fontSize:26,color:"#c9a84c"},
  brandName:{fontFamily:"Georgia,serif",fontSize:"1.3em",fontWeight:700,color:"#e5d5a5"},
  brandSub:{fontSize:"0.65em",color:"#8896b0",marginTop:1},
  headerRight:{display:"flex",alignItems:"center",gap:8},
  langSw:{display:"flex",gap:3,background:"rgba(255,255,255,0.04)",borderRadius:6,padding:3},
  lbtn:{background:"none",border:"none",color:"#8896b0",fontSize:"0.7em",fontWeight:600,padding:"3px 10px",borderRadius:4,cursor:"pointer"},
  lbtnOn:{background:"rgba(201,168,76,0.15)",color:"#c9a84c"},
  histBtn:{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(201,168,76,0.22)",borderRadius:6,color:"#c9a84c",padding:"4px 8px",cursor:"pointer",fontSize:"0.9em"},
  logoutBtn:{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:6,color:"#8896b0",padding:"4px 8px",cursor:"pointer",fontSize:"0.9em"},
  flagsRow:{display:"flex",gap:5,alignItems:"center",flexWrap:"wrap",marginBottom:8,opacity:0.65},
  flag:{fontSize:"0.9em"},
  flagLbl:{fontSize:"0.62em",color:"#8896b0",marginLeft:4},
  tabs:{display:"flex",borderTop:"1px solid rgba(201,168,76,0.22)",margin:"0 -18px"},
  tab:{flex:1,background:"none",border:"none",borderBottom:"2px solid transparent",color:"#8896b0",fontSize:"0.78em",padding:"9px 12px",cursor:"pointer",fontFamily:"Georgia,serif"},
  tabOn:{color:"#c9a84c",borderBottomColor:"#c9a84c",background:"rgba(201,168,76,0.04)"},
  historyPanel:{background:"rgba(8,14,30,0.98)",borderBottom:"1px solid rgba(201,168,76,0.15)",padding:"16px 18px",maxHeight:"300px",overflowY:"auto"},
  historyTitle:{fontSize:"0.85em",color:"#c9a84c",marginBottom:12,fontFamily:"Georgia,serif"},
  historyEmpty:{fontSize:"0.82em",color:"#8896b0",textAlign:"center",padding:"20px"},
  historyItem:{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:8,padding:"10px 14px",marginBottom:8,cursor:"pointer"},
  historyQ:{fontSize:"0.82em",color:"#ddd5c0",marginBottom:4},
  historyDate:{fontSize:"0.7em",color:"#8896b0"},
  main:{flex:1,overflowY:"auto",padding:"20px 16px 8px",display:"flex",flexDirection:"column"},
  welcome:{maxWidth:700,margin:"auto",textAlign:"center",padding:"30px 16px"},
  wicon:{fontSize:"2.8em",color:"#c9a84c",marginBottom:14},
  wtitle:{fontFamily:"Georgia,serif",fontSize:"1.9em",fontWeight:700,color:"#e5d5a5",marginBottom:10},
  wdesc:{color:"#8896b0",fontSize:"0.9em",marginBottom:22,lineHeight:1.7},
  sgrid:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,textAlign:"left"},
  sbtn:{background:"rgba(201,168,76,0.04)",border:"1px solid rgba(201,168,76,0.22)",borderRadius:8,color:"#c9b878",padding:"10px 14px",fontSize:"0.82em",cursor:"pointer",textAlign:"left",fontFamily:"Georgia,serif",lineHeight:1.4},
  msgs:{display:"flex",flexDirection:"column",gap:16,maxWidth:780,width:"100%",margin:"0 auto"},
  mrow:{display:"flex",alignItems:"flex-start",gap:10},
  mrowUser:{flexDirection:"row-reverse"},
  avAi:{width:34,height:34,borderRadius:"50%",background:"linear-gradient(135deg,#1a2a4a,#0d1c38)",border:"1px solid rgba(201,168,76,0.3)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1em",flexShrink:0,color:"#c9a84c"},
  avUser:{width:34,height:34,borderRadius:"50%",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0},
  bubble:{maxWidth:"calc(100% - 50px)",borderRadius:12,padding:"13px 17px",lineHeight:1.75,fontSize:"0.9em"},
  bubbleAi:{background:"rgba(13,21,40,0.85)",border:"1px solid rgba(201,168,76,0.1)",color:"#ddd5c0",borderTopLeftRadius:4},
  bubbleUser:{background:"linear-gradient(135deg,#152a56,#0e1e3e)",border:"1px solid rgba(80,130,255,0.18)",color:"#dde8ff",borderTopRightRadius:4},
  loadingTxt:{fontSize:"0.82em",color:"#8896b0",fontStyle:"italic",marginLeft:4},
  dbadge:{display:"inline-flex",alignItems:"center",gap:6,background:"rgba(201,168,76,0.1)",border:"1px solid rgba(201,168,76,0.22)",borderRadius:4,padding:"2px 8px",fontSize:"0.72em",color:"#c9a84c",marginBottom:8},
  sources:{marginTop:12,paddingTop:10,borderTop:"1px solid rgba(255,255,255,0.07)"},
  sourcesLabel:{fontSize:"0.75em",color:"#c9a84c",marginBottom:6},
  sourceLink:{display:"inline-block",marginRight:8,marginBottom:4,fontSize:"0.72em",color:"#8896b0",textDecoration:"none",background:"rgba(255,255,255,0.04)",padding:"2px 8px",borderRadius:4},
  exportBtn:{background:"rgba(201,168,76,0.1)",border:"1px solid rgba(201,168,76,0.3)",borderRadius:6,color:"#c9a84c",padding:"6px 12px",fontSize:"0.75em",cursor:"pointer",marginTop:10,fontFamily:"Georgia,serif",display:"block"},
  footer:{background:"rgba(5,9,20,0.97)",borderTop:"1px solid rgba(201,168,76,0.15)",padding:"10px 16px 8px",flexShrink:0},
  filePrev:{display:"flex",alignItems:"center",justifyContent:"space-between",background:"rgba(201,168,76,0.08)",border:"1px solid rgba(201,168,76,0.22)",borderRadius:8,padding:"6px 12px",marginBottom:8,fontSize:"0.82em",color:"#c9a84c"},
  fileRm:{background:"none",border:"none",color:"#8896b0",cursor:"pointer"},
  inputRow:{display:"flex",gap:8,maxWidth:780,margin:"0 auto",alignItems:"flex-end"},
  attachBtn:{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(201,168,76,0.22)",borderRadius:10,color:"#c9a84c",width:42,height:42,fontSize:"1.1em",cursor:"pointer",flexShrink:0},
  txa:{flex:1,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(201,168,76,0.25)",borderRadius:10,color:"#ddd5c0",padding:"10px 14px",fontSize:"0.88em",fontFamily:"Georgia,serif",resize:"none",outline:"none",lineHeight:1.6},
  sendBtn:{background:"linear-gradient(135deg,#c9a84c,#9e7830)",border:"none",borderRadius:10,color:"#fff",width:42,height:42,fontSize:"1.1em",cursor:"pointer",flexShrink:0},
  sendOff:{opacity:0.3,cursor:"not-allowed"},
  fnote:{textAlign:"center",fontSize:"0.62em",color:"#3a4a5a",marginTop:8},
};

const CSS = `
@keyframes blink{0%,80%,100%{opacity:0.15}40%{opacity:1}}
.d1{animation:blink 1.3s 0s infinite;color:#c9a84c;font-size:7px;}
.d2{animation:blink 1.3s 0.2s infinite;color:#c9a84c;font-size:7px;}
.d3{animation:blink 1.3s 0.4s infinite;color:#c9a84c;font-size:7px;}
.mdbody .h1{font-size:1.25em;font-weight:700;color:#e5d5a5;margin:12px 0 5px;}
.mdbody .h2{font-size:1.05em;font-weight:600;color:#c9a84c;margin:10px 0 4px;}
.mdbody .li{padding-left:8px;margin:3px 0;}
.mdbody .gap{height:8px;}
.mdbody .mhr{border:none;border-top:1px solid rgba(201,168,76,0.2);margin:10px 0;}
.mdbody .mc{font-family:monospace;background:rgba(201,168,76,0.1);color:#c9a84c;padding:1px 5px;border-radius:3px;}
.mdbody .disc{font-size:0.8em;color:#7a8aaa;margin-top:12px;padding-top:9px;border-top:1px solid rgba(255,255,255,0.06);font-style:italic;}
::-webkit-scrollbar{width:4px;}::-webkit-scrollbar-thumb{background:rgba(201,168,76,0.2);border-radius:4px;}
`;
